import React from "react";
import { DatePicker } from "@jsq/ds/core";
import { parseDateOnlyString, formatDateOnlyString } from "../../../../utils/datetime";
import { createFieldPlaceholder } from "./utils";
import type { FieldEditorProps } from "./types";

/**
 * Date Field Editor Component
 *
 * Specialized input component for date extension fields.
 * Provides date picker with proper formatting and design system styling.
 */
export const DateFieldEditor: React.FC<FieldEditorProps> = ({
  field,
  value,
  onChange,
  error,
  disabled,
}) => {
  // Convert string value to Date object for DatePicker, handle invalid dates gracefully
  // Use parseDateOnlyString to avoid timezone conversion issues
  const dateValue = React.useMemo(() => {
    if (!value) return null;

    // Use our timezone-safe parser for date-only strings
    const parsedDate = parseDateOnlyString(value);
    if (parsedDate) {
      return parsedDate;
    }

    // Fallback for other date formats (shouldn't happen in normal usage)
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return null;
      }
      return date;
    } catch {
      return null;
    }
  }, [value]);

  const handleChange = (date: Date | null) => {
    if (date) {
      // Convert Date object to YYYY-MM-DD format for storage using timezone-safe formatter
      onChange(formatDateOnlyString(date));
    } else {
      onChange("");
    }
  };

  return (
    <>
      <DatePicker
        selected={dateValue}
        onChange={handleChange}
        placeholderText={createFieldPlaceholder(field)}
        disabled={disabled}
        dateFormat="MM/dd/yyyy"
        autoComplete="off"
        isFull
        hasIcon
        data-testid="date-field-editor"
        portalId="jsq-app"
        {...(error && { wrapperClassName: "error" })}
      />
      {error && (
        <div
          role="alert"
          id={`${field.id}-error`}
          style={{ color: "red", fontSize: "14px", marginTop: "4px" }}
        >
          {error}
        </div>
      )}
    </>
  );
};

export default DateFieldEditor;
